import React from 'react';
import _ from 'lodash';
import {
  CalendarIcon,
  SparklesIcon,
  ArrowPathIcon,
  UsersIcon,
  LightBulbIcon,
  ChatBubbleOvalLeftEllipsisIcon,
} from '@heroicons/react-v2/24/solid';

import {InterviewTypes} from '../../../lib/constants/duet';
import DuetHero from '../../public/images/duet_hero.png';
import DuetAIHero from '../../public/images/duet_ai_hero.png';
import DuetRoles from '../../public/images/duet_roles.png';
import DuetFeedback from '../../public/images/duet_feedback.png';
import Maritza from '../../public/images/duet/Maritza.jpeg';
import Yinka from '../../public/images/duet/Yinka.jpg';
import Filipe from '../../public/images/duet/Filipe.jpg';
import {getBrandName} from '../components/Brand';

const getDefaultCopy = (hideAIPracticeFeatures, hideAIFeedbackFeatures) => ({
  title: hideAIPracticeFeatures
    ? 'Mock Interviews: Improve Your Skills By Practicing with Peers'
    : 'Mock Interviews: Improve Your Skills By Practicing with Peers and AI',
  socialDescription: hideAIFeedbackFeatures
    ? `
      Prepare with live mock interviews with thousands of tech professionals.
      Practice real interview questions and get helpful feedback.
    ` : `
      Prepare with live mock interviews with thousands of tech professionals.
      Get feedback from our AI interviewer while you practice real interview
      questions.
  `,
  hero: {
    header: hideAIPracticeFeatures
      ? 'Practice mock interviews with peers'
      : 'Practice mock interviews with peers and AI',
    description: hideAIFeedbackFeatures
      ? `
        Join thousands of tech candidates practicing interviews to land jobs.
        Practice real questions over video chat in a collaborative environment
        with helpful feedback.
      ` : `
      Join thousands of tech candidates practicing interviews to land jobs.
      Practice real questions over video chat in a collaborative environment
      with helpful AI feedback.
    `,
    image: (
      <img
        src={DuetHero.src}
        className="object-contain pointer-events-none mb-6 md:mb-0 md:w-1/2"
        alt={`${getBrandName()} Practice screenshot`}
      />
    ),
    cta: 'Start practicing for free',
  },
  whosUsingIt: {
    header: 'How everyone in tech prepares',
    description: `
      ${getBrandName()} Practice supports interview prep for everyone in tech.
      From product management to software engineering and data roles,
      there are thousands of practice questions to choose from.
    `,
    image: (
      <img
        src={DuetRoles.src}
        className="object-contain pointer-events-none mb-6 md:mb-0 md:w-3/5"
        alt={`${getBrandName()} Practice roles`}
        loading="lazy"
      />
    ),
  },
  howItWorks: {
    header: 'How to schedule a practice session',
    instructions: [
      {
        name: 'Schedule a time',
        description: `
          Join later today or pre-schedule an hour-long mock interview session
          that suits your availability, experience, and skills. Practice anytime
          that fits.
        `,
        icon: CalendarIcon,
      },
      {
        name: 'Get matched',
        description: `
          Automatically match with peers preparing for the same interviews.
          Each match is picked just for you. Take turns role-playing as
          interviewer and interviewee.
        `,
        icon: SparklesIcon,
      },
      {
        name: 'Exchange feedback',
        description: hideAIFeedbackFeatures
          ? `
            Trade detailed notes with your partner using realistic rubrics after the
            session ends. Get honest, actionable feedback from your peer partner.
          ` : `
          Trade detailed notes with your partner using realistic rubrics after the
          session ends. Get honest, actionable feedback from your peer partner and
          our AI.
        `,
        icon: ArrowPathIcon,
      },
    ],
  },
  whyPractice: {
    header: 'Gain confidence and get real results',
    features: [
      {
        name: 'Feedback',
        description: `
          Practice and receive feedback on both your technical and behavioral
          interviewing skills.
        `,
        icon: ChatBubbleOvalLeftEllipsisIcon,
      },
      {
        name: 'Like-minded candidates',
        description: `
          Match with serious, like-minded candidates who are actively improving
          their interview skills.
        `,
        icon: UsersIcon,
      },
      {
        name: 'Deep dive',
        description: `
          Deep dive into the most commonly asked interview questions in tech.
        `,
        icon: LightBulbIcon,
      },
    ],
    image: (
      <img
        src={DuetFeedback.src}
        className="object-contain pointer-events-none ml-10 mb-10 md:ml-0 md:mb-0 w-full"
        alt={`${getBrandName()} Practice feedback screenshot`}
        loading="lazy"
      />
    ),
    cta: 'Start practicing for free',
  },
  testimonials: {
    description: `
      Join thousands of candidates using ${getBrandName()} to practice and land their dream
      jobs.
    `,
    testimonials: [
      {
        name: 'Maritza',
        image: Maritza.src,
        title: 'Product Manager, Microsoft',
        text: `
          Aced's peer-to-peer mock interviews, courses, and coaching sessions
          were everything I needed in one place to get my dream PM job.
        `,
      },
      {
        name: 'Filipe',
        image: Filipe.src,
        title: 'Software Engineer, Google',
        text: `
          Nothing beats mock coding interviews. I wasn't nervous on the day of my
          interviews and got offers from Google and Microsoft.
        `,
      },
      {
        name: 'Yinka',
        image: Yinka.src,
        title: 'Data Scientist, Meta',
        text: `
          I truly benefited from the mock interview practice and coaching sessions.
          Aced gave me everything I needed to succeed.
        `,
      },
    ],
  },
});

const getPMCopy = (defaultCopy) => ({
  title: 'Product Management Mock Interviews: Improve Your Interview Skills',
  hero: {
    header: 'Practice product management mock interviews with peers',
    description: `
      Join thousands of product managers practicing interviews to
      land jobs. Practice real questions over video chat in a
      collaborative environment and get helpful feedback.
    `,
  },
  howItWorks: {
    instructions: [
      defaultCopy.howItWorks.instructions[0],
      {
        description: `
          Automatically match with product managers preparing for the same
          interviews. Each match is picked just for you. Take turns role-playing
          as interviewer and interviewee.
        `,
      },
      defaultCopy.howItWorks.instructions[2],
    ],
  },
});

const getAlgorithmsCopy = (defaultCopy) => ({
  title: 'Coding Mock Interviews: Improve Your Interview Skills',
  hero: {
    header: 'Practice coding mock interviews with peers',
    description: `
      Join thousands of engineers practicing data structures &
      algorithms interviews to land jobs. Practice real questions
      over video chat in a collaborative environment and get helpful
      feedback.
    `,
  },
  howItWorks: {
    instructions: [
      defaultCopy.howItWorks.instructions[0],
      {
        description: `
          Automatically match with engineers preparing for the same
          interviews. Each match is picked just for you. Take turns
          role-playing as interviewer and interviewee.
        `,
      },
      defaultCopy.howItWorks.instructions[2],
    ],
  },
});

const getSystemDesignCopy = (defaultCopy) => ({
  title: 'System Design Mock Interviews: Improve Your Interview Skills',
  hero: {
    header: 'Practice system design mock interviews with peers',
  },
});

const getBehavioralCopy = (defaultCopy) => ({
  title: 'Behavioral Mock Interviews: Improve Your Interview Skills',
  hero: {
    header: 'Practice behavioral mock interviews with peers',
  },
});

const getSQLCopy = (defaultCopy) => ({
  title: 'SQL Mock Interviews: Improve Your Interview Skills',
  hero: {
    header: 'Practice SQL mock interviews with peers',
    description: `
      Join thousands of engineers, business analysts, and data scientists 
      practicing interviews to land jobs. Practice real questions over
      video chat in a collaborative environment and get helpful feedback.
    `,
  },
});

const getDataScienceMLCopy = (defaultCopy) => ({
  title: 'Data Science & ML Mock Interviews: Improve Your Interview Skills',
  hero: {
    header: 'Practice data science & ML mock interviews with peers',
    description: `
      Join thousands of data scientists and machine learning engineers
      practicing SQL interviews to land jobs. Practice real questions over
      video chat in a collaborative environment and get helpful feedback.
    `,
  },
  howItWorks: {
    instructions: [
      defaultCopy.howItWorks.instructions[0],
      {
        description: `
          Automatically match with data scientists and machine learning
          engineers preparing for the same interviews. Each match is picked
          just for you. Take turns role-playing as interviewer and interviewee.
        `,
      },
      defaultCopy.howItWorks.instructions[2],
    ],
  },
});

const getFrontendCopy = (defaultCopy) => ({
  title: 'Frontend Mock Interviews: Improve Your Interview Skills',
  hero: {
    header: 'Practice frontend mock interviews with peers',
    description: `
      Join thousands of frontend engineers practicing interviews to
      land jobs. Practice real questions over video chat in a
      collaborative environment and get helpful feedback.
    `,
  },
  howItWorks: {
    instructions: [
      defaultCopy.howItWorks.instructions[0],
      {
        description: `
          Automatically match with engineers preparing for the same
          interviews. Each match is picked just for you. Take turns
          role-playing as interviewer and interviewee.
        `,
      },
      defaultCopy.howItWorks.instructions[2],
    ],
  },
});

const getInvestmentBankingCopy = (defaultCopy) => ({
  title: 'Investment Banking Mock Interviews: Ace Your Superday',
  hero: {
    header: 'Practice investment banking mock interviews with peers',
    description: `
      Targeting banks like Goldman Sachs, Morgan Stanley, or J.P. Morgan?
      Practice the technical and fit questions top banks ask live over video
      with candidates on the same path.
    `,
  },
  whosUsingIt: null,
  howItWorks: {
    instructions: [
      defaultCopy.howItWorks.instructions[0],
      {
        description: `
          Automatically match with candidates recruiting for the same
          bulge bracket and boutique banks. Each match is picked just for
          you. Take turns role-playing as interviewer and interviewee.
        `,
      },
      defaultCopy.howItWorks.instructions[2],
    ],
  },
  testimonials: null,
});

// TODO: AI-specific FAQ?
const getAICopy = (defaultCopy) => ({
  title: 'AI Mock Interviews: Improve Your Interview Skills',
  hero: {
    header: 'Practice AI mock interviews',
    description: `
      Join thousands of tech candidates using AI to practice interviews
      to land jobs. Rehearse real questions over audio chat and receive
      actionable feedback to improve your answers.
    `,
    cta: 'Start practicing',
    image: (
      <img
        src={DuetAIHero.src}
        className="object-contain pointer-events-none mb-6 md:mb-0 md:w-1/2"
        alt={`${getBrandName()} Practice AI interviews screenshot`}
      />
    ),
  },
  whosUsingIt: {
    description: `
      ${getBrandName()}'s AI Practice supports interview prep for everyone in tech.
      From product management to software engineering and data roles, level 
      up your communication, problem solving, and collaboration skills.
    `,
  },
  howItWorks: {
    header: 'How to practice with AI mock interviews',
    instructions: [
      {
        name: 'Start any time',
        description: 'Start an on-demand interview session at any time that suits your availability and goals. Practice anytime that fits.',
        icon: CalendarIcon,
      },
      {
        name: 'Get assigned a question',
        description: 'Automatically receive a popular interview question. Rotate between other questions as your answers improve.',
        icon: SparklesIcon,
      },
      {
        name: 'Receive feedback and repeat',
        description: 'Get honest, actionable feedback from AI based on realistic interview rubrics after the session ends. Review your notes and transcripts later.',
        icon: ArrowPathIcon,
      },
    ],
  },
  whyPractice: {
    features: [
      {
        name: 'Automatic Feedback',
        description: `
          Practice and receive automatic feedback on key interviewing skills
          like communication, problem solving, and collaboration.
        `,
        icon: ChatBubbleOvalLeftEllipsisIcon,
      },
      {
        name: 'Judgement-free spaces',
        description: `
          Rehearse in a judgement-free space. Refine your answers before an
          upcoming peer-to-peer mock interview session or real interview.
        `,
        icon: UsersIcon,
      },
      {
        name: 'Deep dive',
        description: `
          Deep dive into the most commonly asked behavioral and product
          management interview questions in tech.
        `,
        icon: LightBulbIcon,
      },
    ],
    cta: 'Start practicing',
  },
});

const specificCopy = {
  [InterviewTypes.PM.key]: getPMCopy,
  [InterviewTypes.Algorithms.key]: getAlgorithmsCopy,
  [InterviewTypes['System-Design'].key]: getSystemDesignCopy,
  [InterviewTypes.Behavioral.key]: getBehavioralCopy,
  [InterviewTypes.SQL.key]: getSQLCopy,
  [InterviewTypes['Data-Science-ML'].key]: getDataScienceMLCopy,
  [InterviewTypes.Frontend.key]: getFrontendCopy,
  [InterviewTypes['Investment-Banking'].key]: getInvestmentBankingCopy,
  ai: getAICopy,
};

const getLandingPageCopy = (
  contentId,
  hideAIPracticeFeatures,
  hideAIFeedbackFeatures,
) => {
  const defaultCopy = getDefaultCopy(hideAIPracticeFeatures, hideAIFeedbackFeatures);
  const getSpecificCopy = specificCopy[contentId];

  return getSpecificCopy
    ? _.merge({}, defaultCopy, getSpecificCopy(defaultCopy))
    : defaultCopy;
};

export default getLandingPageCopy;
